/**
 * O catálogo de desenhos internos — o SVG do miolo de um componente no diagrama.
 *
 * São 2.745 desenhos e o acervo está vivo: 109.200 configurações de componente
 * apontam para um deles. A tela tem três decisões que vêm da forma do dado.
 *
 * **A lista não pede o desenho.** 4 MB de SVG somados; pedir tudo para escolher
 * um item seria puxar o acervo inteiro. A coluna "traço" mostra o tamanho, que
 * já responde o que a lista precisa responder — 31 desenhos estão vazios. O SVG
 * chega quando alguém abre um.
 *
 * **O uso aparece em três números, e não em um.** A contagem crua de ligações
 * mente por 23%: 25.309 das 109.200 apontam para um componente que não existe
 * mais. Então a tela mostra os três — o que vale, o tamanho do estrago de uma
 * exclusão, e o lixo — em vez de escolher um e esconder a diferença.
 *
 * **Excluir é um caminho de duas portas.** A primeira recusa e diz quantos
 * componentes e diagramas seguram o desenho; a segunda, com "desprender", faz o
 * que o sistema antigo faz sempre e sem avisar — e aí o operador já viu o
 * número. No desenho mais usado são 14.660 componentes em 3.018 diagramas.
 */

import { useRef, useState } from "react"
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import {
  alterarDesenhoInterno, criarDesenhoInterno, diagramasDoDesenho, excluirDesenhoInterno,
  listarDesenhosInternos, obterDesenhoInterno,
  type DesenhoInterno, type DesenhoInternoResumo, type SalvarDesenhoInterno,
} from "@/api/desenhosInternos"
import { ErroDaApi } from "@/api/erros"
import { AvisoDeDadoGlobal } from "@/kit/AvisoDeDadoGlobal"
import { BarraDeEstado } from "@/kit/BarraDeEstado"
import { Faixa } from "@/kit/Faixa"
import {
  SeletorDeOrdem, usarOrdemDaLista, usarRecemIncluido, usarRolarAteRecente,
} from "@/kit/ordemDaLista"
import {
  Aviso, Botao, Campo, ColunaDeLista, ItemDaLista, Painel, Secao,
  estiloCampo, mensagem,
} from "../outros/pecas"

const VAZIO: SalvarDesenhoInterno = {
  titulo: "", desenho: "", qtdPinos: 0, qtdPinosOposto: 0,
  posicao: "left", alturaMinima: 0, referencia: 1,
  componentePrincipal: false, deslocarPino: false,
}

function bytes(n: number) {
  if (n === 0) return "sem traço"
  return n < 1024 ? `${n} B` : `${(n / 1024).toFixed(1)} kB`
}

export function DesenhosInternos() {
  const qc = useQueryClient()
  const [texto, setTexto] = useState("")
  const [busca, setBusca] = useState("")
  const [posicao, setPosicao] = useState("")
  const [semUso, setSemUso] = useState<"" | "sim" | "nao">("")
  const [abertoId, setAbertoId] = useState<number | null>(null)
  const [rascunho, setRascunho] = useState<SalvarDesenhoInterno | null>(null)
  const [verDiagramas, setVerDiagramas] = useState(false)
  const [recado, setRecado] = useState<string | null>(null)
  const [podeDesprender, setPodeDesprender] = useState(false)
  const [ordem, setOrdem] = usarOrdemDaLista("desenhos-internos")
  const recente = usarRecemIncluido()
  const rolagem = useRef<HTMLDivElement>(null)

  // A ordem vai para a API: a lista corta em 200 de 2.745, e ordenar aqui só
  // reordenaria o recorte.
  const lista = useQuery({
    queryKey: ["desenhos-internos", busca, posicao, semUso, ordem] as const,
    queryFn: () => listarDesenhosInternos({
      busca, posicao,
      semUso: semUso === "" ? undefined : semUso === "sim",
      porPagina: 200,
      ordem,
    }),
  })

  const aberto = useQuery({
    queryKey: ["desenho-interno", abertoId] as const,
    queryFn: () => obterDesenhoInterno(abertoId!),
    enabled: abertoId != null,
  })

  const usos = useQuery({
    queryKey: ["desenho-interno-diagramas", abertoId] as const,
    queryFn: () => diagramasDoDesenho(abertoId!),
    enabled: abertoId != null && verDiagramas,
  })

  const salvar = useMutation({
    mutationFn: (d: SalvarDesenhoInterno) =>
      abertoId == null ? criarDesenhoInterno(d) : alterarDesenhoInterno(abertoId, d),
    onSuccess: (d, dados) => {
      qc.invalidateQueries({ queryKey: ["desenhos-internos"] })
      qc.invalidateQueries({ queryKey: ["desenho-interno"] })
      if (abertoId == null) {
        // Um filtro escolhido antes poderia esconder justamente o que acabou de entrar —
        // "só os em uso" esconde todo desenho novo.
        setTexto(""); setBusca(""); setPosicao(""); setSemUso("")
        recente.marcar(d.id)
        setRecado(`Desenho “${dados.titulo}” incluído — id ${d.id}.`)
      } else {
        setRecado(`Desenho ${d.id} gravado.`)
      }
      setAbertoId(d.id)
      setRascunho(null)
    },
    onError: (e) => setRecado(mensagem(e)),
  })

  const excluir = useMutation({
    mutationFn: (desprender: boolean) => excluirDesenhoInterno(abertoId!, desprender),
    onSuccess: (desprendidos) => {
      qc.invalidateQueries({ queryKey: ["desenhos-internos"] })
      setAbertoId(null)
      setPodeDesprender(false)
      setRecado(desprendidos > 0
        ? `Desenho excluído. ${desprendidos} componente(s) ficaram sem traço.`
        : "Desenho excluído.")
    },
    onError: (e) => {
      setRecado(mensagem(e))
      // A recusa por uso e o caminho normal, nao um erro de operacao: e ela que
      // abre a segunda porta, ja com o numero na tela.
      if (e instanceof ErroDaApi && e.status === 400) setPodeDesprender(true)
    },
  })

  const itens = lista.data?.itens ?? []
  const total = lista.data?.total ?? 0
  usarRolarAteRecente(rolagem, recente.id, itens)

  function abrir(id: number) {
    setAbertoId(id); setRascunho(null); setVerDiagramas(false); setPodeDesprender(false)
  }

  return (
    <div style={{ flex: 1, minHeight: 0, display: "flex", flexDirection: "column" }}>
      <Faixa
        titulo="Desenhos internos"
        detalhe={lista.data
          ? `${itens.length} de ${total.toLocaleString("pt-BR")} — o traço de cada um chega ao abrir`
          : "O SVG do miolo do componente dentro do diagrama"}
      >
        <input
          value={texto}
          onChange={(e) => setTexto(e.target.value)}
          onKeyDown={(e) => { if (e.key === "Enter") setBusca(texto.trim()) }}
          placeholder="Título do desenho"
          style={{ ...estiloCampo, width: 240 }}
        />
        <Botao aoClicar={() => setBusca(texto.trim())}>Procurar</Botao>

        <select value={posicao} onChange={(e) => setPosicao(e.target.value)}
                style={{ ...estiloCampo, width: 120 }}>
          <option value="">Os dois lados</option>
          <option value="left">left</option>
          <option value="right">right</option>
        </select>

        <select
          value={semUso}
          onChange={(e) => setSemUso(e.target.value as "" | "sim" | "nao")}
          style={{ ...estiloCampo, width: 180 }}
          title="Os sem uso são os únicos que saem sem mexer em diagrama de ninguém"
        >
          <option value="">Usados e não usados</option>
          <option value="sim">Só os sem uso</option>
          <option value="nao">Só os em uso</option>
        </select>

        <SeletorDeOrdem valor={ordem} aoMudar={setOrdem} estilo={{ height: "auto", padding: "4px 7px", borderRadius: 6 }} />

        <Botao destaque aoClicar={() => { setAbertoId(null); setRascunho({ ...VAZIO }); setVerDiagramas(false) }}>
          Novo
        </Botao>
      </Faixa>

      <AvisoDeDadoGlobal oQueMuda="O desenho interno é do acervo inteiro: alterar um muda o traço em todos os diagramas que o usam" />

      <div style={{ flex: 1, minHeight: 0, display: "flex" }}>
        <ColunaDeLista largura={380}>
          {lista.isPending ? (
            <p style={{ fontSize: 12, color: "var(--txt3)" }}>Carregando…</p>
          ) : lista.error ? (
            <p style={{ fontSize: 12, color: "var(--erro)" }}>{mensagem(lista.error)}</p>
          ) : itens.length === 0 ? (
            <p style={{ fontSize: 12, color: "var(--txt3)" }}>Nenhum desenho com esse filtro.</p>
          ) : (
            <div ref={rolagem}>{itens.map((d) => (
              <ItemDaLista
                key={d.id}
                recente={d.id === recente.id}
                ativo={d.id === abertoId}
                aoClicar={() => abrir(d.id)}
                titulo={d.titulo || "(sem título)"}
                detalhe={<Resumo d={d} />}
              />
            ))}</div>
          )}
        </ColunaDeLista>

        <Painel>
          {rascunho ? (
            <Formulario
              id={abertoId}
              rascunho={rascunho}
              aoEditar={setRascunho}
              aoSalvar={() => salvar.mutate(rascunho)}
              salvando={salvar.isPending}
              aoCancelar={() => setRascunho(null)}
            />
          ) : abertoId == null ? (
            <p style={{ fontSize: 12.5, color: "var(--txt3)", maxWidth: "60ch", lineHeight: 1.6 }}>
              Escolha um desenho na lista. A lista não carrega o traço — são 4 MB somados —,
              então o SVG só chega quando você abre um.
            </p>
          ) : aberto.isPending ? (
            <p style={{ fontSize: 12.5, color: "var(--txt3)" }}>Carregando…</p>
          ) : aberto.error ? (
            <p style={{ fontSize: 12.5, color: "var(--erro)" }}>{mensagem(aberto.error)}</p>
          ) : aberto.data ? (
            <Ficha
              d={aberto.data}
              aoEditar={() => setRascunho({
                titulo: aberto.data.titulo, desenho: aberto.data.desenho,
                qtdPinos: aberto.data.qtdPinos, qtdPinosOposto: aberto.data.qtdPinosOposto,
                posicao: aberto.data.posicao, alturaMinima: aberto.data.alturaMinima,
                referencia: aberto.data.referencia,
                componentePrincipal: aberto.data.componentePrincipal,
                deslocarPino: aberto.data.deslocarPino,
              })}
              aoExcluir={() => excluir.mutate(false)}
              excluindo={excluir.isPending}
              podeDesprender={podeDesprender}
              aoDesprender={() => excluir.mutate(true)}
              verDiagramas={verDiagramas}
              aoVerDiagramas={() => setVerDiagramas((v) => !v)}
              diagramas={usos.data}
              carregandoDiagramas={usos.isPending && verDiagramas}
            />
          ) : null}
        </Painel>
      </div>

      <BarraDeEstado direita={`${total.toLocaleString("pt-BR")} desenho(s) no acervo`}>
        {recado ?? "Excluir recusa enquanto houver uso — e diz quantos diagramas perderiam o traço."}
      </BarraDeEstado>
    </div>
  )
}

// ------------------------------------------------------------------ pedaços

function Resumo({ d }: { d: DesenhoInternoResumo }) {
  return (
    <span style={{ fontFamily: "var(--fm)", fontSize: 10.5 }}>
      #{d.id} · {d.qtdPinos}{d.qtdPinosOposto > 0 ? `+${d.qtdPinosOposto}` : ""} pinos · {d.posicao}
      {" · "}
      <span style={{ color: d.tamanhoDoDesenho === 0 ? "var(--erro)" : "var(--txt3)" }}>
        {bytes(d.tamanhoDoDesenho)}
      </span>
      {" · "}
      <span style={{ color: d.emUso === 0 ? "var(--txt3)" : undefined }}>
        {d.emUso === 0 ? "sem uso" : `${d.emUso} em ${d.diagramas} diagrama(s)`}
      </span>
      {d.orfaos > 0 && <span style={{ color: "var(--txt3)" }}> · {d.orfaos} órfã(s)</span>}
    </span>
  )
}

function Ficha(props: {
  d: DesenhoInterno
  aoEditar: () => void
  aoExcluir: () => void
  excluindo: boolean
  podeDesprender: boolean
  aoDesprender: () => void
  verDiagramas: boolean
  aoVerDiagramas: () => void
  diagramas?: { itens: import("@/api/desenhosInternos").DiagramaDoDesenho[]; total: number }
  carregandoDiagramas: boolean
}) {
  const { d } = props

  return (
    <>
      <div style={{ display: "flex", alignItems: "baseline", gap: 10, marginBottom: 4 }}>
        <h2 style={{ margin: 0, fontSize: 17, fontWeight: 600 }}>{d.titulo || "(sem título)"}</h2>
        <span style={{ fontFamily: "var(--fm)", fontSize: 11.5, color: "var(--txt3)" }}>#{d.id}</span>
      </div>
      <p style={{ margin: "0 0 14px", fontSize: 11.5, color: "var(--txt2)" }}>
        {d.qtdPinos}{d.qtdPinosOposto > 0 ? ` + ${d.qtdPinosOposto} do lado oposto` : ""} pino(s) ·
        lado {d.posicao} · âncora no {d.referenciaTexto} · altura mínima {d.alturaMinima} ·
        {d.componentePrincipal ? " componente principal" : " componentes laterais"} ·
        {d.deslocarPino ? " desloca o pino" : " não desloca"}
      </p>

      <Secao titulo="Gabarito" contagem={d.desenho ? `${d.desenho.split("#").length} peça(s)` : undefined}>
        <Gabarito texto={d.desenho} />
      </Secao>

      <Secao titulo="Onde é usado">
        <div style={{ display: "flex", gap: 22 }}>
          <Numero valor={d.emUso} rotulo="componentes" />
          <Numero valor={d.diagramas} rotulo="diagramas" />
          <Numero
            valor={d.orfaos}
            rotulo="ligações órfãs"
            dica="Ligações cujo componente de diagrama não existe mais. São 23% do acervo — é por isso que a contagem crua exagera o uso."
          />
        </div>
      </Secao>

      {d.faqs.length > 0 && (
        <Secao titulo="FAQ" contagem={d.faqs.length}>
          <ul style={{ margin: 0, paddingLeft: 18, fontSize: 12.5, lineHeight: 1.6 }}>
            {d.faqs.map((f) => <li key={f.faqId}>{f.pergunta || `#${f.faqId}`}</li>)}
          </ul>
        </Secao>
      )}

      <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginTop: 4 }}>
        <Botao destaque aoClicar={props.aoEditar}>Editar</Botao>
        <Botao aoClicar={props.aoVerDiagramas} desligado={d.diagramas === 0}>
          {props.verDiagramas ? "Esconder os diagramas" : `Ver os ${d.diagramas} diagrama(s)`}
        </Botao>
        <Botao aoClicar={props.aoExcluir} ocupado={props.excluindo}>Excluir</Botao>
      </div>

      {props.podeDesprender && (
        <div style={{ marginTop: 12 }}>
          <Aviso>
            <strong>O desenho está em uso.</strong> Excluir mesmo assim tira o traço de{" "}
            <strong>{d.emUso} componente(s) em {d.diagramas} diagrama(s)</strong> que ninguém
            tocou. É o que o sistema antigo faz sempre, e sem avisar.
          </Aviso>
          <Botao aoClicar={props.aoDesprender} ocupado={props.excluindo}>
            Desprender e excluir mesmo assim
          </Botao>
        </div>
      )}

      {props.verDiagramas && (
        <Secao titulo="Diagramas que usam" contagem={props.diagramas?.total}>
          {props.carregandoDiagramas ? (
            <p style={{ fontSize: 12, color: "var(--txt3)" }}>Carregando…</p>
          ) : (
            <ul style={{ margin: 0, paddingLeft: 18, fontSize: 12, lineHeight: 1.7 }}>
              {(props.diagramas?.itens ?? []).map((x) => (
                <li key={x.diagramaId}>
                  <span style={{ fontFamily: "var(--fm)" }}>#{x.diagramaId}</span> ·{" "}
                  {x.projeto || "(projeto sumiu)"} · {x.componenteCodigo} {x.componenteDescricao}
                  {x.quantos > 1 && <span style={{ color: "var(--txt3)" }}> ×{x.quantos}</span>}
                </li>
              ))}
            </ul>
          )}
        </Secao>
      )}
    </>
  )
}

function Formulario(props: {
  id: number | null
  rascunho: SalvarDesenhoInterno
  aoEditar: (d: SalvarDesenhoInterno) => void
  aoSalvar: () => void
  salvando: boolean
  aoCancelar: () => void
}) {
  const r = props.rascunho
  const mudar = (p: Partial<SalvarDesenhoInterno>) => props.aoEditar({ ...r, ...p })

  return (
    <>
      <h2 style={{ margin: "0 0 14px", fontSize: 17, fontWeight: 600 }}>
        {props.id == null ? "Novo desenho interno" : `Editando o desenho ${props.id}`}
      </h2>

      <Secao titulo="Identificação">
        <Campo rotulo="Título" ajuda="É por ele que o desenho é achado. Repete muito no acervo — seja específico.">
          <input value={r.titulo} onChange={(e) => mudar({ titulo: e.target.value })} style={estiloCampo} />
        </Campo>
      </Secao>

      <Secao titulo="Geometria">
        <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
          <Campo rotulo="Pinos">
            <input type="number" min={0} value={r.qtdPinos}
                   onChange={(e) => mudar({ qtdPinos: Number(e.target.value) })} style={estiloCampo} />
          </Campo>
          <Campo rotulo="Pinos do lado oposto">
            <input type="number" min={0} value={r.qtdPinosOposto}
                   onChange={(e) => mudar({ qtdPinosOposto: Number(e.target.value) })} style={estiloCampo} />
          </Campo>
          <Campo rotulo="Altura mínima">
            <input type="number" min={0} value={r.alturaMinima}
                   onChange={(e) => mudar({ alturaMinima: Number(e.target.value) })} style={estiloCampo} />
          </Campo>
        </div>

        <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
          <Campo rotulo="Lado" ajuda="O desenho não conhece outro valor.">
            <select value={r.posicao} onChange={(e) => mudar({ posicao: e.target.value })} style={estiloCampo}>
              <option value="left">left</option>
              <option value="right">right</option>
            </select>
          </Campo>
          <Campo rotulo="Âncora" ajuda="De onde o desenho se posiciona.">
            <select value={r.referencia} onChange={(e) => mudar({ referencia: Number(e.target.value) })} style={estiloCampo}>
              <option value={1}>Primeiro pino</option>
              <option value={0}>Centro</option>
            </select>
          </Campo>
          <Campo rotulo="Qual componente">
            <select value={r.componentePrincipal ? "1" : "0"}
                    onChange={(e) => mudar({ componentePrincipal: e.target.value === "1" })} style={estiloCampo}>
              <option value="0">Componentes laterais</option>
              <option value="1">Componente principal</option>
            </select>
          </Campo>
          <Campo rotulo="Deslocar pino">
            <select value={r.deslocarPino ? "1" : "0"}
                    onChange={(e) => mudar({ deslocarPino: e.target.value === "1" })} style={estiloCampo}>
              <option value="0">Não deslocar</option>
              <option value="1">Deslocar pino</option>
            </select>
          </Campo>
        </div>
      </Secao>

      <Secao titulo="Gabarito do traço">
        <Campo
          rotulo="Gabarito"
          ajuda="Coordenadas entre chaves, peças separadas por #. Coordenada NaN vira zero antes de gravar — um NaN apaga o traço inteiro na hora de desenhar."
        >
          <textarea
            value={r.desenho ?? ""}
            onChange={(e) => mudar({ desenho: e.target.value })}
            rows={12}
            style={{ ...estiloCampo, fontFamily: "var(--fm)", fontSize: 11.5, resize: "vertical" }}
          />
        </Campo>
      </Secao>

      <div style={{ display: "flex", gap: 8 }}>
        <Botao destaque aoClicar={props.aoSalvar} ocupado={props.salvando}>
          {props.salvando ? "Gravando…" : "Gravar"}
        </Botao>
        <Botao aoClicar={props.aoCancelar}>Cancelar</Botao>
      </div>
    </>
  )
}

/**
 * O gabarito, e não um desenho — é isso que está gravado.
 *
 * O campo guarda **um molde**, não SVG pronto: as coordenadas vêm entre chaves
 * (`{Ix-6}`, `{Iy2}`, `{0.5}`) e o motor as substitui na hora de desenhar, com a
 * posição que o componente tiver naquele diagrama. `I` é a primeira ligação,
 * `L`/`A` são largura e altura — quem escolhe é a âncora do desenho. E o `#`
 * separa as peças.
 *
 * Por isso não há pré-visualização: fora de um diagrama não existe onde ancorar,
 * e mostrar um quadro vazio faria o operador achar que o desenho está quebrado.
 */
function Gabarito({ texto }: { texto: string }) {
  if (!texto) {
    return (
      <p style={{ margin: 0, fontSize: 12, color: "var(--erro)" }}>
        Este desenho não tem gabarito. São 31 assim no acervo.
      </p>
    )
  }

  return (
    <>
      <p style={{ margin: "0 0 6px", fontSize: 11.5, color: "var(--txt2)", maxWidth: "72ch", lineHeight: 1.55 }}>
        É um molde, não um desenho pronto: <code>{"{Ix-6}"}</code> e <code>{"{Iy2}"}</code> viram
        coordenadas quando o motor coloca a peça num diagrama — <code>I</code> conta da primeira
        ligação, <code>L</code> e <code>A</code> da largura e da altura, e a âncora do desenho diz
        qual vale. Fora de um diagrama não há onde ancorar, e por isso não há prévia aqui.
      </p>
      <pre
        style={{
          margin: 0, padding: 10, maxHeight: 260, overflow: "auto",
          border: "1px solid var(--line2)", borderRadius: 6, background: "var(--surface)",
          fontFamily: "var(--fm)", fontSize: 11, lineHeight: 1.5, whiteSpace: "pre-wrap",
          wordBreak: "break-all",
        }}
      >
        {texto.split("#").map((p) => p.trim()).filter(Boolean).join("\n\n")}
      </pre>
    </>
  )
}

function Numero({ valor, rotulo, dica }: { valor: number; rotulo: string; dica?: string }) {
  return (
    <span title={dica} style={{ display: "flex", flexDirection: "column", cursor: dica ? "help" : undefined }}>
      <strong style={{ fontFamily: "var(--fm)", fontSize: 18, fontVariantNumeric: "tabular-nums" }}>
        {valor.toLocaleString("pt-BR")}
      </strong>
      <span style={{ fontSize: 11, color: "var(--txt3)" }}>{rotulo}</span>
    </span>
  )
}
